import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { reviews } from '@/mocks/reviews';
import { useRef } from 'react';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    const formData = new FormData(form);
    try {
      const response = await fetch('http://bjgjfh.com/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 基德漫画网 | 好看韩漫漫画免费漫画平台"
        description="基德漫画网是专业的韩漫免费漫画在线观看平台，提供下拉式阅读体验。了解我们的品牌故事、用户口碑和联系方式。好看韩漫漫画免费漫画尽在基德漫画网。"
        keywords="基德漫画网,好看韩漫,漫画免费漫画,韩漫免费漫画,在线观看下拉式,关于我们"
        canonicalUrl="http://bjgjfh.com/about"
        schema={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于基德漫画网',
            description: '基德漫画网是专业的韩漫免费漫画在线观看平台',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '基德漫画网',
            url: 'http://bjgjfh.com/',
            description: '基德漫画网提供好看韩漫漫画免费漫画，支持韩漫免费漫画在线观看下拉式阅读体验',
            contactPoint: {
              '@type': 'ContactPoint',
              email: 'hello@bjgjfh.com',
              contactType: 'customer service',
              availableLanguage: ['Chinese'],
            },
          },
        ]}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <p className="text-xs text-secondary-600 mb-3 font-medium uppercase tracking-wider">
                  关于基德漫画网
                </p>
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-5 leading-tight">
                  致力于打造最好的
                  <strong className="font-heading text-primary-600">韩漫免费漫画</strong>
                  阅读平台
                </h1>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  基德漫画网成立于2024年，是一个专注于韩国漫画（韩漫）的免费在线阅读平台。我们致力于为广大漫画爱好者提供最优质的阅读体验，支持韩漫免费漫画在线观看下拉式阅读，让每一部韩漫作品都能以最佳的方式呈现给读者。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                  我们的团队由一群热爱漫画的年轻人组成，深知漫画迷对阅读体验的追求。从页面设计到内容筛选，每一个环节都精益求精。平台目前收录超过2000部优质韩漫作品，涵盖恋爱、奇幻、动作、悬疑、搞笑、治愈等热门类型。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  基德漫画网坚持免费阅读的理念，让每一位漫画爱好者都能无门槛地享受韩漫的魅力。我们采用先进的下拉式阅读技术，告别繁琐的翻页，让阅读更加流畅自然。
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-[480px] flex gap-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20art%20studio%20illustration%20with%20warm%20cozy%20lighting%2C%20creative%20workspace%20with%20drawing%20tablet%20and%20art%20supplies%2C%20minimalist%20composition%2C%20warm%20coral%20and%20cream%20tones%2C%20artistic%20atmosphere%2C%20Korean%20webtoon%20aesthetic&width=600&height=450&seq=about-img-01&orientation=landscape"
                  alt="基德漫画网品牌形象"
                  className="w-[45%] rounded-lg object-cover object-center"
                />
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20reading%20experience%20illustration%20with%20warm%20cozy%20lighting%2C%20digital%20reading%20on%20tablet%20with%20webtoon%20display%2C%20minimalist%20composition%2C%20warm%20amber%20tones%2C%20comfortable%20atmosphere%2C%20Korean%20webtoon%20aesthetic&width=600&height=450&seq=about-img-02&orientation=landscape"
                  alt="韩漫下拉式阅读体验"
                  className="w-[55%] rounded-lg object-cover object-center mt-8"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-3xl text-foreground-950 mb-8">
              用户口碑
            </h2>
            <div className="flex flex-col gap-6">
              {reviews.map((review) => (
                <article key={review.id} className="bg-background-100 rounded-lg p-6 text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <img
                      src={review.avatar}
                      alt={review.userName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{review.userName}</p>
                      <p className="text-xs text-foreground-500">{review.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed italic">
                    &ldquo;{review.content}&rdquo;
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20with%20warm%20sunset%20lighting%2C%20peaceful%20reading%20scene%20with%20warm%20coral%20and%20golden%20tones%2C%20minimalist%20composition%2C%20calm%20atmosphere%2C%20Korean%20webtoon%20aesthetic%20background%2C%20inviting%20and%20warm%20mood&width=1920&height=750&seq=cta-about-contact&orientation=landscape"
            alt="联系我们"
            title="联系基德漫画网"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 max-w-xl">
              <h2 className="font-heading text-xl md:text-3xl text-white mb-3">
                联系我们
              </h2>
              <p className="text-sm text-white/70 mb-6">
                有任何问题或建议？欢迎随时与我们取得联系
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-white/80">
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line"></i>
                  </div>
                  hello@bjgjfh.com
                </span>
                <span className="hidden sm:block text-white/30">|</span>
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-customer-service-line"></i>
                  </div>
                  在线客服：工作日 9:00-18:00
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-xl mx-auto">
            <h2 className="font-heading text-xl md:text-3xl text-foreground-950 mb-3 text-center">
              给我们留言
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              您的每一条反馈都是我们进步的动力
            </p>

            {formStatus === 'success' ? (
              <div className="bg-accent-100 text-accent-800 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-3xl text-accent-600"></i>
                </div>
                <p className="text-sm font-medium">感谢您的留言！我们会尽快回复。</p>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-4">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div>
                  <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    您的昵称
                  </label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的昵称"
                  />
                </div>
                <div>
                  <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    电子邮箱
                  </label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="about-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言（最多500字）"
                  ></textarea>
                </div>
                {formStatus === 'error' && formError && (
                  <p className="text-xs text-red-600">{formError}</p>
                )}
                <button
                  type="submit"
                  className="w-full px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  提交留言
                </button>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}